import type {
  RecurringExpensesControllerActivateParams,
  RecurringExpensesControllerCancelParams,
  RecurringExpensesControllerGetOneParams,
  RecurringExpensesControllerPauseParams,
  RecurringExpensesControllerRemoveParams,
  RecurringExpensesControllerUpdateParams,
} from '@trellis/api'
import type {
  CreateRecurringExpense,
  RecurringExpenseListQuery,
  RecurringExpenseResponse,
  RecurringExpenseRunResponse,
  UpdateRecurringExpense,
} from '@trellis/shared'

import { apiClient } from '@/lib/api-client'
import { request } from '@/lib/request'

export const listRecurringExpenses = (params: RecurringExpenseListQuery = {}) =>
  request<RecurringExpenseResponse[]>(() =>
    // The generated list method omits query from its type signature (Swagger doesn't
    // pick up @ApiQuery on this controller). Pass query via FullRequestParams spread —
    // the HttpClient processes it correctly at runtime.
    apiClient.api.recurringExpensesControllerList({ query: params } as never),
  )

export const getRecurringExpense = (id: string) =>
  request<RecurringExpenseResponse>(() =>
    apiClient.api.recurringExpensesControllerGetOne({
      id,
    } as RecurringExpensesControllerGetOneParams),
  )

export const createRecurringExpense = (data: CreateRecurringExpense) =>
  request<RecurringExpenseResponse>(() =>
    apiClient.api.recurringExpensesControllerCreate(data as never),
  )

export const updateRecurringExpense = (id: string, data: UpdateRecurringExpense) =>
  request<RecurringExpenseResponse>(() =>
    apiClient.api.recurringExpensesControllerUpdate(
      { id } as RecurringExpensesControllerUpdateParams,
      data as never,
    ),
  )

export const activateRecurringExpense = (id: string) =>
  request<RecurringExpenseResponse>(() =>
    apiClient.api.recurringExpensesControllerActivate({
      id,
    } as RecurringExpensesControllerActivateParams),
  )

export const pauseRecurringExpense = (id: string) =>
  request<RecurringExpenseResponse>(() =>
    apiClient.api.recurringExpensesControllerPause({
      id,
    } as RecurringExpensesControllerPauseParams),
  )

export const cancelRecurringExpense = (id: string) =>
  request<RecurringExpenseResponse>(() =>
    apiClient.api.recurringExpensesControllerCancel({
      id,
    } as RecurringExpensesControllerCancelParams),
  )

export const deleteRecurringExpense = (id: string) =>
  request<void>(() =>
    apiClient.api.recurringExpensesControllerRemove({
      id,
    } as RecurringExpensesControllerRemoveParams),
  )

export const triggerRecurringExpense = (id: string) =>
  request<RecurringExpenseResponse>(() =>
    apiClient.api.request({
      path: `/api/recurring-expenses/${id}/trigger`,
      method: 'POST',
      format: 'json',
    }),
  )

export const listRecurringExpenseRuns = (id: string) =>
  request<RecurringExpenseRunResponse[]>(() =>
    // Runs endpoint added after last client regen; call via HttpClient.request directly
    apiClient.api.request({
      path: `/api/recurring-expenses/${id}/runs`,
      method: 'GET',
      format: 'json',
    }),
  )

export const getRecurringExpenseStats = () =>
  request<{ total: number; active: number; paused: number }>(() =>
    apiClient.api.request({
      path: `/api/recurring-expenses/stats`,
      method: 'GET',
      secure: true,
      format: 'json',
    }),
  )
